import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Authenticator from "../../../services/Authenticator";
import { fetchOrgCharts } from "../../../store/actions/orgChart/orgChartActions";
import OrgChartTree, { OrgChartList } from "./OrgChartTree";
import { alsoInMap, chainOf, peersOf, searchItems, formatDate, peopleCount, HR_LEVELS } from "./orgChartModel";
import "./OrgChart.css";

const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.5;

/**
 * Organizational Chart — Dashboard tab (everyone). Shows the published charts HR maintains:
 * a Chart dropdown, the people tree (fits the width by default), Find a person, Where am I?,
 * and who someone reports to / who is on the same level. HR also gets "Manage charts".
 */
class OrgChart extends Component {
  constructor(props) {
    super(props);
    this.state = { chartId: null, selectedId: null, closed: {}, zoom: "fit", query: "", notice: null };
    this.canvasRef = React.createRef();
    this.applyZoom = this.applyZoom.bind(this);
  }

  componentDidMount() {
    this.props.fetchOrgCharts();
    window.addEventListener("resize", this.applyZoom);
  }

  componentDidUpdate() {
    this.applyZoom();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.applyZoom);
  }

  charts() {
    const store = this.props.orgChart || {};
    return Array.isArray(store.charts) ? store.charts : [];
  }

  current() {
    const charts = this.charts();
    return charts.find((c) => c.id === this.state.chartId) || charts[0] || null;
  }

  /** Scales the chart to the card width ("fit") or to the chosen zoom, keeping it centred. */
  applyZoom() {
    const canvas = this.canvasRef.current;
    const stage = canvas ? canvas.querySelector(".oc-stage") : null;
    if (!stage) return;
    stage.style.transform = "none";
    stage.style.height = "";
    stage.style.marginLeft = "";
    const width = stage.scrollWidth;
    const height = stage.scrollHeight;
    const room = canvas.clientWidth - 48;
    const zoom = this.state.zoom === "fit" ? Math.min(1, room > 0 && width > 0 ? room / width : 1) : this.state.zoom;
    this.appliedZoom = zoom;
    stage.style.transform = "scale(" + zoom + ")";
    stage.style.height = height * zoom + "px";
    if (zoom < 1) stage.style.marginLeft = Math.max(0, (room - width * zoom) / 2) + "px";
  }

  zoomBy = (step) => {
    const now = this.appliedZoom || 1;
    this.setState({ zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.round((now + step) * 10) / 10)) });
  };

  openChart = (id) => this.setState({ chartId: id, selectedId: null, closed: {}, zoom: "fit", query: "", notice: null });

  onSelect = (item) => this.setState((st) => ({ selectedId: st.selectedId === item.id ? null : item.id, notice: null }));

  onToggle = (id) => this.setState((st) => ({ closed: { ...st.closed, [id]: !st.closed[id] } }));

  allItems(chart) {
    return chart ? chart.items.concat(...(chart.groups || []).map((g) => g.items)) : [];
  }

  /** Jumps to the logged-in employee: the current chart first, otherwise the first chart they are in. */
  whereAmI = () => {
    const me = this.props.userId;
    const charts = this.charts();
    const current = this.current();
    const inChart = (c) => this.allItems(c).find((i) => i.user_id === me);
    const chart = current && inChart(current) ? current : charts.find(inChart);
    if (!me || !chart) {
      this.setState({ notice: "You are not on any published chart yet." });
      return;
    }
    const item = inChart(chart);
    // open every branch above me so my card is visible
    const closed = {};
    this.setState({ chartId: chart.id, selectedId: item.id, closed, notice: null, query: "" });
  };

  pickFound = (item) => this.setState({ selectedId: item.id, query: "", closed: {} });

  details(chart) {
    const { selectedId } = this.state;
    if (this.state.notice) return <span role="status">{this.state.notice}</span>;
    if (!selectedId) return <span className="text-muted">Select a person to see who they report to and who is on the same level.</span>;
    const items = this.allItems(chart);
    const me = items.find((i) => i.id === selectedId);
    if (!me) return null;
    const boss = chart.items.find((i) => i.id === me.parent_id);
    const peers = peersOf(chart.items, me.id);
    const level = chain => chain.length + 1;
    const inGroup = (chart.groups || []).find((g) => g.items.some((i) => i.id === me.id));
    return (
      <span role="status">
        <b>{me.name}</b>{me.title ? " · " + me.title : ""}
        {inGroup ? " · " + inGroup.name : " · level " + level(chainOf(chart.items, me.id))}
        {boss ? <> · reports to <b>{boss.name}</b></> : null}
        {peers.length ? " · same level: " + peers.map((p) => p.name).join(", ") : ""}
      </span>
    );
  }

  render() {
    const store = this.props.orgChart || {};
    const isHr = !!Authenticator.scanLevel(HR_LEVELS);

    if (!store.isLoaded) {
      return (
        <div className="org-chart">
          <div className="oc-card">
            {store.loadError ? (
              <p className="oc-state" role="status">Couldn't load the organizational chart. Refresh the page to try again.</p>
            ) : (
              <p className="oc-state" role="status" aria-busy="true"><i className="fa fa-spinner fa-spin" aria-hidden="true" />Loading the organizational chart…</p>
            )}
          </div>
        </div>
      );
    }

    const charts = this.charts();
    const chart = this.current();
    const manageLink = isHr && (
      <Link to={global.links.org_chart_manage} className="btn btn-primary" style={{ height: 32, lineHeight: "20px" }}>
        <i className="fa fa-cog" aria-hidden="true" />Manage charts
      </Link>
    );

    if (!chart) {
      return (
        <div className="org-chart">
          <div className="oc-card oc-empty">
            <i className="fa fa-sitemap" aria-hidden="true" />
            <h3>No organizational chart yet</h3>
            <p>{isHr ? "Create a chart and publish it to show it here." : "HR is preparing it. Please check back soon."}</p>
            {manageLink}
          </div>
        </div>
      );
    }

    const alsoIn = alsoInMap(charts);
    const found = searchItems(this.allItems(chart), this.state.query).slice(0, 8);
    const asOf = formatDate(chart.as_of);

    return (
      <div className="org-chart">
        <div className="oc-card">
          <div className="oc-bar">
            <div>
              <h3 className="oc-head"><i className="fa fa-sitemap" aria-hidden="true" />{chart.name}</h3>
              <div className="oc-meta"><b>{peopleCount(chart)}</b> people{asOf ? " · as of " + asOf : ""}</div>
            </div>
            <div className="oc-tools">
              <div>
                <label className="oc-label" htmlFor="oc-chart">Chart:</label>
                <select id="oc-chart" className="form-control" style={{ width: 230 }} value={chart.id} onChange={(e) => this.openChart(Number(e.target.value))}>
                  {charts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="oc-find">
                <label className="oc-label" htmlFor="oc-find">Find a person:</label>
                <input id="oc-find" className="form-control" style={{ width: 190 }} placeholder="Type a name" autoComplete="off"
                  value={this.state.query} onChange={(e) => this.setState({ query: e.target.value })} />
                {this.state.query.trim().length >= 2 && (
                  <ul className="oc-find-list" role="listbox" aria-label="Matching people">
                    {found.length === 0 && <li className="px-2 py-1 text-muted">Nobody called "{this.state.query.trim()}" in this chart.</li>}
                    {found.map((i) => (
                      <li key={i.id}><button type="button" onClick={() => this.pickFound(i)}>{i.name}<small>{i.title}</small></button></li>
                    ))}
                  </ul>
                )}
              </div>
              {manageLink}
            </div>
          </div>

          <div className="oc-sub">
            <button type="button" className="btn btn-primary-2 btn-sm" onClick={this.whereAmI}>
              <i className="fa fa-crosshairs" aria-hidden="true" />Where am I?
            </button>
            <span className="oc-sp" />
            <span className="oc-zoom">Zoom</span>
            <span className="oc-zoom">
              <button type="button" className="btn btn-light border btn-icon" onClick={() => this.zoomBy(-0.1)} aria-label="Zoom out"><i className="fa fa-minus" aria-hidden="true" /></button>{" "}
              <button type="button" className="btn btn-light border btn-sm" onClick={() => this.setState({ zoom: "fit" })}>Fit</button>{" "}
              <button type="button" className="btn btn-light border btn-icon" onClick={() => this.zoomBy(0.1)} aria-label="Zoom in"><i className="fa fa-plus" aria-hidden="true" /></button>
            </span>
          </div>

          <div className="oc-canvas" ref={this.canvasRef}>
            <OrgChartTree
              items={chart.items}
              groups={chart.groups}
              detailRows={chart.detail_rows || []}
              selectedId={this.state.selectedId}
              onSelect={this.onSelect}
              closed={this.state.closed}
              onToggle={this.onToggle}
              alsoIn={alsoIn}
              onOpenChart={this.openChart}
              chartId={chart.id}
              meUserId={this.props.userId}
            />
          </div>
          <OrgChartList items={chart.items} groups={chart.groups} detailRows={chart.detail_rows || []} closed={this.state.closed} onToggle={this.onToggle}
            onSelect={this.onSelect} meUserId={this.props.userId} />

          <div className="oc-info">
            {this.details(chart)}
            <div className="oc-legend" aria-hidden="true">
              <span><i style={{ boxShadow: "0 0 0 2px #82af13" }} />Selected</span>
              <span><i style={{ borderColor: "#07335e" }} />Reports up to</span>
              <span><i style={{ background: "#effafb", borderColor: "#9fd3da" }} />Same level</span>
              <span><i style={{ background: "#0097a7", borderColor: "#0097a7" }} />Team</span>
              <span><i style={{ borderStyle: "dashed" }} />Open position</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  orgChart: state.orgChart,
  userId: state.user && state.user.id ? Number(state.user.id) : null,
});

const mapDispatchToProps = (dispatch) => ({
  fetchOrgCharts: () => dispatch(fetchOrgCharts()),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrgChart);
